import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import axiosService from '../../inits/axios';

import {
  createPiece,
  getPieceByID,
  editPiece,
  deleteCurrentPiece,
  createInterview,
  deleteInterview,
  editPieceInterview,
  createCloseups,
  deleteCloseups,
  editPieceCloseups,
  pieceSubscription,
  getPieceClosupByID,
  getPieceInterviewByID,
  getPieceSubscriptionByID,
} from '../../store/actions/piece/piece.action';
import { getComposers } from '../../store/actions/composer/composer.action';
import { getDifficulties } from '../../store/actions/difficulties/difficulties.action';
import { getEras } from '../../store/actions/era/era.action';
import { getInstruments } from '../../store/actions/instrument/instrument.action';
import { getMembers } from '../../store/actions/member/member.action';
import {
  getAwsKey,
  createPieceVideo,
  deletePieceVideo,
  editPieceVideo,
} from '../../store/actions/piece-video/piece-video.action';
import { getArtists, getArtistByID } from '../../store/actions/artist/artist.action';
import {
  createPieceInstrument,
  deletePieceInstrument,
  editPieceInstrument,
} from '../../store/actions/piece-instrument/piece-instrument.action';
import { getSubscriptions } from '../../store/actions/subscription/subscription.action';
import CreatePiece from '../../components/Pieces/CreatePiece';
import CreateInstruments from '../../components/Pieces/CreateInstruments';
import CreateVideos from '../../components/Pieces/CreateVideos';
import CreateInterview from '../../components/Pieces/CreateInterview';
import CreateCloseups from '../../components/Pieces/CreateCloseups';
import CreateSubscriptions from '../../components/Pieces/CreateSubscriptions';

import PageLoader from '../../components/Common/PageLoader';

import '../../styles/piece.css';

const CreatePiecesContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    isCreating,
    currentPiece,
    eras,
    difficulties,
    composers,
    instruments,
    artists,
    subscriptions,
    categories,
    members,
    pieces_categories,
  } = useSelector(state => ({
    isCreating: state.piece.isCreating,
    currentPiece: state.piece.currentPiece,
    eras: state.era.list,
    difficulties: state.difficulties.list,
    composers: state.composer.list,
    instruments: state.instrument.list,
    artists: state.artist.list,
    subscriptions: state.subscription.list,
    categories: state.auth.categories,
    members: state.member.list,
    pieces_categories: state.piece.pieces_categories,
  }));

  const [state, setState] = useState({
    mode: 'CREATE',
    pieceToBeEdit: null,
    startRender: false,
    field: 'pieces',
    accessMode: '',
    accessDenied: false,
    artistInfo: null,
    pieceInterviews: [],
    pieceCloseups: [],
    pieceSubscriptions: [],
  });

  const setAccess = useCallback(() => {
    try {
      const categoryData = categories.find(category => category.link === 'pieces');
      if (categoryData) {
        setState(prev => ({
          ...prev,
          accessMode: categoryData.mode,
          accessDenied: false
        }));
      } else {
        setState(prev => ({ ...prev, accessDenied: true }));
      }
    } catch (err) {
      setState(prev => ({ ...prev, accessDenied: true }));
    }
  }, [categories]);

  const getPieceAttribute = useCallback(async (mode, pieceId) => {
    try {
      await Promise.all([
        dispatch(getMembers()),
        dispatch(getComposers()),
        dispatch(getDifficulties()),
        dispatch(getEras()),
        dispatch(getInstruments()),
        dispatch(getArtists()),
        dispatch(getSubscriptions()),
      ]);

      if (mode === 'EDIT') {
        await getPiece(mode, pieceId);
      } else {
        setState(prev => ({
          ...prev,
          mode,
          startRender: true,
        }));
      }
    } catch (error) {
      navigate('/pieces');
    }
  }, [dispatch, navigate]);

  const getPiece = useCallback(async (mode, pieceID) => {
    try {
      const pieceToBeEdit = await dispatch(getPieceByID(pieceID));
      const artistInfo = await dispatch(getArtistByID(pieceToBeEdit.artist_id));

      let pieceInterviews = [],
        pieceCloseups = [],
        pieceSubscriptions = [];

      try {
        pieceInterviews = await axiosService.get(`/pieces/interviews?piece_id=${pieceID}`);
      } catch (err) { }

      try {
        pieceCloseups = await axiosService.get(`/pieces/closeups?piece_id=${pieceID}`);
      } catch (err) { }

      try {
        pieceSubscriptions = await axiosService.get(`/pieces/subscriptions?piece_id=${pieceID}`);
      } catch (err) { }

      setState(prev => ({
        ...prev,
        pieceToBeEdit,
        mode,
        startRender: true,
        artistInfo,
        pieceInterviews,
        pieceCloseups,
        pieceSubscriptions,
      }));
    } catch (error) {
      // navigate('/pieces');
    }
  }, [dispatch]);

  const triggerGetPiece = useCallback(() => {
    const mode = id ? 'EDIT' : 'CREATE';
    getPiece(mode, id);
  }, [id, getPiece]);

  const handleCreateInterview = useCallback(async (interviewInfo) => {
    await dispatch(createInterview(interviewInfo));
    getInterviews();
  }, [dispatch]);

  const handleDeleteInterview = useCallback(async (interviewInfo) => {
    await dispatch(deleteInterview(interviewInfo));
    getInterviews();
  }, [dispatch]);

  const getInterviews = useCallback(async () => {
    const pieceInterviews = await axiosService.get(`/pieces/interviews?piece_id=${state.pieceToBeEdit.id}`);
    setState(prev => ({
      ...prev,
      pieceInterviews,
    }));
  }, [state.pieceToBeEdit?.id]);

  const handleCreateCloseups = useCallback(async (closeupsInfo) => {
    await dispatch(createCloseups(closeupsInfo));
    getCloseups();
  }, [dispatch]);

  const handleDeleteCloseups = useCallback(async (closeupsInfo) => {
    await dispatch(deleteCloseups(closeupsInfo));
    getCloseups();
  }, [dispatch]);

  const getCloseups = useCallback(async () => {
    const pieceCloseups = await axiosService.get(`/pieces/closeups?piece_id=${state.pieceToBeEdit.id}`);
    setState(prev => ({
      ...prev,
      pieceCloseups,
    }));
  }, [state.pieceToBeEdit?.id]);

  const showField = (fieldName) => {
    setState(prev => ({
      ...prev,
      field: fieldName,
    }));
  };

  const createPieceSubscriptionUtil = useCallback(async (f1, f2, f3, f4) => {
    try {
      await dispatch(pieceSubscription(f1, f2, f3, f4));
      const mode = id ? 'EDIT' : 'CREATE';
      getPiece(mode, id);
      return Promise.resolve();
    } catch (err) { }
  }, [dispatch, id, getPiece]);

  useEffect(() => {
    const mode = id ? 'EDIT' : 'CREATE';
    getPieceAttribute(mode, id);
    setAccess();
  }, [id, getPieceAttribute, setAccess]);

  useEffect(() => {
    setAccess();
  }, [setAccess]);

  useEffect(() => {
    if (currentPiece && state.pieceToBeEdit) {
      setState(prev => ({
        ...prev,
        pieceToBeEdit: currentPiece,
      }));
    }
  }, [currentPiece, state.pieceToBeEdit]);

  useEffect(() => {
    return () => {
      dispatch(deleteCurrentPiece());
    };
  }, [dispatch]);

  if (!state.startRender) {
    return <PageLoader />;
  }

  const { artistInfo, pieceInterviews, pieceCloseups, accessDenied, accessMode } = state;
  const piecesInfo = { eras, members, difficulties, composers, instruments, artists };
  const pieceInstrumentInfo = {
    instruments,
    piece: currentPiece,
    getAwsKey: (params) => dispatch(getAwsKey(params)),
    getCurrentPiece: getPiece,
  };

  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <div className="row">
        <div className="navBarPiece">
          <div
            className={state.field === 'pieces' ? 'navBarField navBarFieldActive' : 'navBarField'}
            onClick={() => showField('pieces')}
          >
            <div className="navBarHeading">Piece</div>
          </div>
          <div className={state.field === 'instruments' ? 'navBarField navBarFieldActive' : 'navBarField'}>
            <div className="navBarHeading" onClick={() => showField('instruments')}>
              Instruments
            </div>
          </div>
          <div className={state.field === 'videos' ? 'navBarField navBarFieldActive' : 'navBarField'}>
            <div className="navBarHeading" onClick={() => showField('videos')}>
              Videos
            </div>
          </div>
          <div className={state.field === 'interviews' ? 'navBarField navBarFieldActive' : 'navBarField'}>
            <div className="navBarHeading" onClick={() => showField('interviews')}>
              Interviews
            </div>
          </div>
          <div className={state.field === 'closeups' ? 'navBarField navBarFieldActive' : 'navBarField'}>
            <div className="navBarHeading" onClick={() => showField('closeups')}>
              Closeups
            </div>
          </div>
          <div className={state.field === 'subscriptions' ? 'navBarField navBarFieldActive' : 'navBarField'}>
            <div className="navBarHeading" onClick={() => showField('subscriptions')}>
              Subscriptions
            </div>
          </div>
        </div>
        <div className="col-sm-12">
          {state.field === 'pieces' && (
            <CreatePiece
              accessMode={accessMode}
              onCreate={(data) => dispatch(createPiece(data))}
              onEdit={(data) => dispatch(editPiece(data))}
              initialValues={state.pieceToBeEdit}
              mode={state.mode}
              piecesInfo={piecesInfo}
              isCreating={isCreating}
              getAwsKey={(params) => dispatch(getAwsKey(params))}
              pieces_categories={pieces_categories}
            />
          )}
        </div>
        {state.field === 'instruments' && (
          <div className="col-sm-12">
            {currentPiece ? (
              <CreateInstruments
                accessMode={accessMode}
                mode={state.mode}
                piecesInfo={piecesInfo}
                onCreate={(data) => dispatch(createPieceInstrument(data))}
                onEdit={(data) => dispatch(editPieceInstrument(data))}
                pieceInstrumentInfo={pieceInstrumentInfo}
                onDelete={(data) => dispatch(deletePieceInstrument(data))}
              />
            ) : (
              <h1>Please Add Piece</h1>
            )}
          </div>
        )}
        {state.field === 'videos' && (
          <div className="col-sm-12">
            {currentPiece ? (
              <CreateVideos
                accessMode={accessMode}
                mode={state.mode}
                piecesInfo={piecesInfo}
                pieceInstrumentInfo={pieceInstrumentInfo}
                onCreate={(data) => dispatch(createPieceVideo(data))}
                onEdit={(data) => dispatch(editPieceVideo(data))}
                onDelete={(data) => dispatch(deletePieceVideo(data))}
              />
            ) : (
              <h1>Please Add Piece</h1>
            )}
          </div>
        )}
        {state.field === 'interviews' && (
          <div className="col-sm-12">
            {currentPiece ? (
              <CreateInterview
                accessMode={accessMode}
                currentPiece={currentPiece}
                artistInfo={artistInfo}
                piecesInfo={piecesInfo}
                onCreate={handleCreateInterview}
                onDelete={handleDeleteInterview}
                getAwsKey={(params) => dispatch(getAwsKey(params))}
                onEdit={(data) => dispatch(editPieceInterview(data))}
                pieceInterviews={pieceInterviews}
                triggerGetPiece={triggerGetPiece}
              />
            ) : (
              <h1>Please Add Piece</h1>
            )}
          </div>
        )}
        {state.field === 'closeups' && (
          <div className="col-sm-12">
            {currentPiece ? (
              <CreateCloseups
                accessMode={accessMode}
                currentPiece={currentPiece}
                artistInfo={artistInfo}
                piecesInfo={piecesInfo}
                instruments={instruments}
                onCreate={handleCreateCloseups}
                onDelete={handleDeleteCloseups}
                getAwsKey={(params) => dispatch(getAwsKey(params))}
                pieceCloseups={pieceCloseups}
                onEdit={(data) => dispatch(editPieceCloseups(data))}
                triggerGetPiece={triggerGetPiece}
              />
            ) : (
              <h1>Please Add Piece</h1>
            )}
          </div>
        )}
        {state.field === 'subscriptions' && (
          <div className="col-sm-12">
            {currentPiece ? (
              <CreateSubscriptions
                accessMode={accessMode}
                currentPiece={currentPiece}
                subscriptions={subscriptions}
                pieceSubscriptions={state.pieceSubscriptions}
                pieceAddDelete={createPieceSubscriptionUtil}
              />
            ) : (
              <h1>Please Add Piece</h1>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default CreatePiecesContainer;
